import type { SyntheticEvent, ReactNode } from 'react'
import { Eye, ClipboardCheck, CloudOff } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/features/workshop/utils/format-datetime'
import type { ReviewItem } from '../types/review.types'
import { AuthorAvatar } from './author-avatar'

type Props = {
  item: ReviewItem & { status?: string }
  onView: (engineId: string) => void
  onReview?: (id: string) => void
  onUnpublish?: (item: ReviewItem & { status?: string }) => void
  unpublishDisabled?: boolean
  showReviewAction?: boolean
  showUnpublishAction?: boolean
  showStatus?: boolean
  className?: string
}

export function ReviewCard({
  item,
  onView,
  onReview,
  onUnpublish,
  unpublishDisabled = false,
  showReviewAction = true,
  showUnpublishAction = false,
  showStatus = false,
  className,
}: Props) {
  const createdAtText = item.createdAt ? formatDateTime(item.createdAt) : '—'
  const statusLabel = getStatusLabel(item.status)
  const statusVariant = getStatusVariant(item.status)

  const stop = (e: SyntheticEvent) => e.stopPropagation()

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => onView(item.id)} // ✅ id 就是 engineId
      aria-label={`查看世界卡：${item.name}`}
      className={cn(
        'group bg-background overflow-hidden rounded-2xl border transition hover:shadow-sm',
        'focus-visible:ring-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        className
      )}
    >
      {/* ===== Cover ===== */}
      <div className='bg-muted relative aspect-[3/4] w-full overflow-hidden'>
        {item.coverUrl ? (
          <img
            src={item.coverUrl}
            alt={item.name}
            className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]'
            loading='lazy'
          />
        ) : (
          <div className='text-muted-foreground flex h-full w-full items-center justify-center text-sm'>
            暂无封面
          </div>
        )}

        {/* 顶部渐变 */}
        <div className='from-background/70 via-background/25 pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b to-transparent' />

        {/* 操作按钮 */}
        <div
          className='absolute top-3 right-3 flex items-center gap-2 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100'
          onClick={stop}
          onKeyDown={stop}
        >
          <IconButton
            label='查看详情'
            onClick={() => onView(item.id)}
            icon={<Eye className='h-4 w-4' />}
          />
          {showReviewAction && onReview ? (
            <IconButton
              label='审核'
              onClick={() => onReview(item.id)} // ✅ id（engineId）
              icon={<ClipboardCheck className='h-4 w-4' />}
            />
          ) : null}
          {showUnpublishAction && onUnpublish ? (
            <IconButton
              label='下架引擎'
              onClick={() => onUnpublish(item)}
              disabled={unpublishDisabled}
              destructive
              icon={<CloudOff className='h-4 w-4' />}
            />
          ) : null}
        </div>
      </div>

      {/* ===== Bottom Info ===== */}
      <div className='space-y-3 p-4'>
        <div className='flex items-start justify-between gap-2'>
          <div className='text-base leading-snug font-semibold'>{item.name}</div>
          {showStatus ? (
            <Badge variant={statusVariant} className='mt-0.5'>
              {statusLabel}
            </Badge>
          ) : null}
        </div>

        <div className='text-muted-foreground line-clamp-2 text-sm'>
          {item.description}
        </div>

        {/* Author */}
        <div className='flex items-center gap-2'>
          <AuthorAvatar
            src={item.authorAvatarUrl}
            name={item.authorName}
            size={28}
          />
          <div className='text-muted-foreground text-xs'>{item.authorName}</div>
        </div>

        <div className='text-muted-foreground text-xs tabular-nums'>
          创建于 {createdAtText}
        </div>

      </div>
    </div>
  )
}

function getStatusLabel(status?: string) {
  switch (status) {
    case 'reviewing':
      return '待审核'
    case 'draft':
      return '草稿'
    case 'published':
      return '已发布'
    default:
      return status?.trim() || '未知状态'
  }
}

function getStatusVariant(status?: string) {
  switch (status) {
    case 'reviewing':
      return 'default'
    case 'published':
      return 'secondary'
    case 'draft':
      return 'outline'
    default:
      return 'outline'
  }
}

function IconButton({
  label,
  icon,
  onClick,
  disabled,
  destructive,
}: {
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
}) {
  return (
    <button
      type='button'
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'bg-background/80 flex h-9 w-9 items-center justify-center rounded-xl border backdrop-blur',
        'text-muted-foreground transition',
        'hover:bg-muted hover:text-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50',
        destructive &&
        'hover:bg-destructive/10 hover:text-destructive hover:border-destructive/40'
      )}
    >
      {icon}
    </button>
  )
}
